import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, ShieldCheck, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import API from "@/apiConfig";

const REGULATIONS = [
  "Nothing may be attached to the walls (no staples, tacks, tape, etc.).",
  "Rollable backdrop may NOT be moved or used by renters.",
  "No sub-renting to third parties.",
  "Renter is fully responsible for guest conduct and any damage.",
  "Smoking strictly prohibited inside (including e-cigarettes). Allowed only in designated outdoor areas.",
  "Renter must set up and fully restore the room to original condition.",
  "Return all tables, chairs, and dishes. Sweep floors. Remove all trash to the dumpster in front of The Master's Blend (not behind it).",
  "Access is limited to the day and time of rental — includes setup, event, and cleanup only.",
];

export default function RentalContractPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [depositToken, setDepositToken] = useState("");
  const [countdown, setCountdown] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/rentals/contract/${token}`)
      .then((r) => {
        setContract(r.data);
        if (r.data.deposit_token) setDepositToken(r.data.deposit_token);
      })
      .catch((e) => setError(e.response?.data?.detail || "Contract not found or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-redirect countdown
  useEffect(() => {
    if (countdown === null || !depositToken) return;
    if (countdown <= 0) {
      navigate(`/rental/deposit/${depositToken}`);
      return;
    }
    timerRef.current = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [countdown, depositToken, navigate]);

  // Return visit: start countdown if already signed but not yet paid
  useEffect(() => {
    if (!loading && contract?.status === "contract_signed" && depositToken && countdown === null) {
      setCountdown(3);
    }
  }, [loading, contract?.status, depositToken, countdown]);

  const handleSign = async () => {
    setSigning(true);
    try {
      const res = await axios.post(`${API}/rentals/contract/${token}/sign`);
      setContract((prev) => ({ ...prev, status: res.data.status, signed_at: res.data.signed_at }));
      const dt = res.data.deposit_token || depositToken;
      if (dt) setDepositToken(dt);
      toast.success("Contract signed! Redirecting to deposit payment\u2026");
      if (dt) setCountdown(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sign. Please try again.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-navy mb-2">Contract Unavailable</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const isSigned = contract?.status === "signed" || contract?.status === "contract_signed" || contract?.status === "deposit_paid" || contract?.status === "full_paid";

  return (
    <div className="min-h-screen bg-bone" data-testid="contract-page">
      {/* Header */}
      <div className="bg-navy py-8 text-center">
        <h1 className="font-heading text-2xl sm:text-3xl text-white">Word Alive Global Center</h1>
        <p className="text-gold text-sm mt-1">Community Room Rental Agreement</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {isSigned ? (
          <div className="text-center py-10" data-testid="contract-signed">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-navy mb-2">Agreement Signed</h2>
            <p className="text-gray-500 mb-1">Signed on {new Date(contract.signed_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>
            {(contract.status === "contract_signed") && depositToken ? (
              <div className="mt-6">
                {countdown !== null && countdown > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-6 animate-in fade-in" data-testid="redirect-countdown">
                    <p className="text-green-800 text-sm font-medium flex items-center justify-center gap-2">
                      <ArrowRight className="h-4 w-4" />
                      Contract signed successfully. Now choose your payment option &rarr;
                    </p>
                    <p className="text-green-600 text-xs mt-1">Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}&hellip;</p>
                  </div>
                )}
                <div className="bg-gold/10 border border-gold/30 rounded-sm p-5 text-left mb-6">
                  <p className="text-navy font-heading text-base mb-2">Next Step: Secure Your Date</p>
                  <p className="text-gray-600 text-sm">Choose to pay the full rental now (recommended) or secure with a $50 non-refundable booking fee.</p>
                </div>
                <a
                  href={`/rental/deposit/${depositToken}`}
                  onClick={() => clearTimeout(timerRef.current)}
                  className="inline-block w-full sm:w-auto bg-gold text-navy border-2 border-gold font-bold py-4 px-10 rounded text-lg uppercase tracking-wider hover:bg-yellow-500 transition-colors no-underline text-center shadow-md"
                  data-testid="pay-deposit-link"
                >
                  Pay Now to Secure Your Date
                </a>
              </div>
            ) : (contract.status === "deposit_paid" || contract.status === "full_paid" || contract.status === "signed") ? (
              <p className="text-green-600 text-sm mt-4 font-medium">Your booking is fully confirmed. Check your email for WiFi access and key pickup details.</p>
            ) : (
              <p className="text-gray-500 text-sm">Check your email at <strong className="text-navy">{contract.email}</strong> for next steps.</p>
            )}
          </div>
        ) : (
          <>
            {/* Booking Summary */}
            <div className="bg-white rounded-sm border border-bone-dark p-6 mb-6" data-testid="contract-summary">
              <h2 className="font-heading text-xl text-navy mb-4">Booking Details</h2>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between"><span className="text-gray-500">Renter</span><span className="text-navy font-medium">{contract.name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Event</span><span className="text-navy font-medium">{contract.event_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="text-navy font-medium">{contract.event_date}</span></div>
                {contract.event_time && <div className="flex justify-between"><span className="text-gray-500">Time</span><span className="text-navy">{contract.event_time}</span></div>}
                {contract.guests > 0 && <div className="flex justify-between"><span className="text-gray-500">Expected Guests</span><span className="text-navy">{contract.guests}</span></div>}
                {contract.deposit_amount > 0 && (
                  <div className="flex justify-between border-t border-bone-dark pt-2 mt-2">
                    <span className="text-gray-500">Deposit Paid</span>
                    <span className="text-green-600 font-medium">${contract.deposit_amount}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Regulations */}
            <div className="bg-white rounded-sm border border-bone-dark p-6 mb-6" data-testid="contract-regulations">
              <h2 className="font-heading text-xl text-navy mb-4">Rental Agreement & Regulations</h2>
              <p className="text-gray-600 text-sm mb-4">
                By signing below, you agree to the following terms for use of The Community Room at Word Alive Global Center, 1128 W Panola St, Carthage, TX 75633:
              </p>
              <div className="space-y-3">
                {REGULATIONS.map((rule, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <ShieldCheck className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                    <p className="text-sm text-gray-700 leading-relaxed">{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Terms */}
            <div className="bg-white rounded-sm border border-bone-dark p-6 mb-6">
              <h2 className="font-heading text-xl text-navy mb-4">Payment Terms</h2>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex gap-2"><span className="text-gold shrink-0">&#x2022;</span>$50 nonrefundable deposit required to hold date (deducted from total). Paid securely online.</li>
                <li className="flex gap-2"><span className="text-gold shrink-0">&#x2022;</span>Full payment due within 10 business days of deposit.</li>
                <li className="flex gap-2"><span className="text-gold shrink-0">&#x2022;</span>Cancellations: Full refund minus $50 deposit if canceled 48+ hours in advance.</li>
              </ul>
            </div>

            {/* Sign Button */}
            <div className="bg-white rounded-sm border-2 border-gold/30 p-6 text-center" data-testid="contract-sign-section">
              <p className="text-gray-600 text-sm mb-4">
                By clicking "I Agree & Sign" below, I confirm that I have read, understand, and agree to all terms, regulations, and payment policies stated above. I accept full responsibility for the Community Room during my rental period.
              </p>
              <Button
                data-testid="contract-sign-btn"
                className="btn-gold px-12 py-3 uppercase tracking-wider font-semibold text-sm"
                onClick={handleSign}
                disabled={signing}
              >
                {signing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {signing ? "Signing..." : "I Agree & Sign"}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="bg-navy py-4 text-center">
        <p className="text-gold text-xs">1128 W Panola Street, Carthage, TX 75633</p>
        <p className="text-white/40 text-xs mt-1">Word Alive Global Center</p>
      </div>
    </div>
  );
}
